import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getCurrentUser } from '../data/session';
import { usePhases } from '../contexts/PhaseContext';

const usePhaseSafe = () => {
  try {
    return usePhases();
  } catch {
    return null;
  }
};

const DashboardIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
  </svg>
);

const JourneyIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M4 12h4l3-9 4 18 3-9h4" />
  </svg>
);

const CoursesIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
  </svg>
);

const SessionsIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <path d="M9 16l2 2 4-4" />
  </svg>
);

const StartupIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M2.81 14.126l5.482 5.482c.39.39 1.023.39 1.414 0l9.126-9.126c.39-.39.39-1.023 0-1.414l-5.482-5.482c-.39-.39-1.023-.39-1.414 0L2.81 12.712c-.39.39-.39 1.023 0 1.414zM7.05 6.636L5.636 8.05l1.414 1.414L8.464 8.05 7.05 6.636z"/>
  </svg>
);

const PitchIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
    <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
  </svg>
);

const UsersIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
  </svg>
);

const MentorsIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C9.24 2 7 4.24 7 7s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0 2c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 10c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    <path d="M17.5 11l-1.41-1.41L14 11.67l-1.09-1.08L11.5 12l2.5 2.5L17.5 11z"/>
  </svg>
);

const PhasesIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
    <rect x="9" y="3" width="6" height="4" rx="1" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const InvestorsIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
  </svg>
);

const ApplicationsIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
    <rect x="9" y="3" width="6" height="4" rx="1" />
    <path d="M9 12h6M9 16h4" />
  </svg>
);

const AnalyticsIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M16,11.78L20.24,4.45L21.97,5.45L16.74,14.5L10.23,10.75L5.46,19H22V21H2V3H4V17.54L9.5,8L16,11.78Z"/>
  </svg>
);


const CoursesAdminIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zM5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z"/>
  </svg>
);

const ChevronDoubleLeftIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="11 17 6 12 11 7" />
    <polyline points="18 17 13 12 18 7" />
  </svg>
);

const ChevronDoubleRightIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="13 17 18 12 13 7" />
    <polyline points="6 17 11 12 6 7" />
  </svg>
);

interface SidebarProps {
  expanded: boolean;
  onToggle: () => void;
}

const Sidebar = ({ expanded, onToggle }: SidebarProps) => {
  const location = useLocation();
  const role = getCurrentUser().role;
  const phaseCtx = usePhaseSafe();

  const isPitchUnlocked = (() => {
    if (!phaseCtx) return false;
    const pitchPhase = phaseCtx.phases.find(p => p.type === 'pitching');
    return pitchPhase ? phaseCtx.isPhaseUnlocked(pitchPhase.id) : false;
  })();

  const allItems = [
    // Founder
    { path: '/app/dashboard/founder',  label: 'My Journey',       icon: JourneyIcon,        roles: ['founder'] as const },
    { path: '/app/lms/courses',        label: 'Learning',         icon: CoursesIcon,        roles: ['founder'] as const },
    { path: '/app/sessions',           label: 'Sessions',         icon: SessionsIcon,       roles: ['founder'] as const },
    { path: '/app/startup',            label: 'Business Profile', icon: StartupIcon,        roles: ['founder'] as const },
    { path: '/app/pitch/idea',         label: 'Pitch',            icon: PitchIcon,          roles: ['founder'] as const },
    // Mentor
    { path: '/app/dashboard/mentor',   label: 'Dashboard',        icon: DashboardIcon,      roles: ['mentor'] as const },
    { path: '/app/sessions',           label: 'Sessions',         icon: SessionsIcon,       roles: ['mentor'] as const },
    // Admin
    { path: '/app/dashboard/admin',    label: 'Dashboard',        icon: DashboardIcon,      roles: ['admin'] as const },
    { path: '/app/admin/users',        label: 'Users',            icon: UsersIcon,          roles: ['admin'] as const },
    { path: '/app/admin/mentors',      label: 'Mentors',          icon: MentorsIcon,        roles: ['admin'] as const },
    { path: '/app/admin/investors',    label: 'Investors',        icon: InvestorsIcon,      roles: ['admin'] as const },
    { path: '/app/admin/courses',      label: 'Courses',          icon: CoursesAdminIcon,   roles: ['admin'] as const },
    { path: '/app/admin/phases',       label: 'Phases',           icon: PhasesIcon,         roles: ['admin'] as const },
    { path: '/app/admin/sessions',      label: 'Sessions',         icon: SessionsIcon,       roles: ['admin'] as const },
    { path: '/app/admin/applications',  label: 'Applications',     icon: ApplicationsIcon,   roles: ['admin'] as const },
    { path: '/app/analytics',           label: 'Analytics',        icon: AnalyticsIcon,      roles: ['admin'] as const },
    // Investor
    { path: '/app/dashboard/investor', label: 'Dashboard',        icon: DashboardIcon,      roles: ['investor'] as const },
  ];

  const menuItems = allItems.filter(i => {
    if (!(i.roles as readonly string[]).includes(role)) return false;
    if (i.path === '/app/pitch/idea' && role === 'founder' && !isPitchUnlocked) return false;
    return true;
  });

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <aside
      className={`${expanded ? 'w-56' : 'w-16'} transition-all duration-300 ease-in-out bg-shell border-r border-line flex-col hidden md:flex flex-shrink-0 relative`}
    >
      {/* Logo */}
      <div className={`flex justify-center items-center flex-shrink-0 ${expanded ? 'py-4 px-4' : 'py-2 px-2'}`}>
        <img
          src="/logo.png"
          alt="BunyadKar"
          className={`object-contain ${expanded ? 'h-[120px] w-[120px]' : 'h-14 w-14'}`}
        />
      </div>

      {/* Toggle tab */}
      <button
        onClick={onToggle}
        title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        className="absolute top-8 -right-6 z-10 flex items-center justify-center w-6 h-10 bg-shell border border-line border-l-0 rounded-r-lg text-muted hover:text-text hover:bg-surface transition-colors shadow-sm"
      >
        {expanded
          ? <ChevronDoubleLeftIcon className="w-3 h-3" />
          : <ChevronDoubleRightIcon className="w-3 h-3" />
        }
      </button>

      {/* Navigation Menu */}
      <nav className="flex-1 px-2 overflow-hidden">
        <ul className="space-y-1">
          {menuItems.map((item) => (
            <li key={item.path + item.label}>
              <Link
                to={item.path}
                title={expanded ? undefined : item.label}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'text-chart bg-accent-soft'
                    : 'text-text/80 hover:text-text hover:bg-surface'
                } ${expanded ? '' : 'justify-center'}`}
              >
                <item.icon className={`${expanded ? 'w-5 h-5' : 'w-6 h-6'} flex-shrink-0`} />
                <span className={`font-medium whitespace-nowrap transition-all duration-200 ${expanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                  {item.label}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;
